/**
 * Diaper Inspector Page
 * Real-time monitoring of clip sensor data received from relays
 */

import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useClipDataStore } from '../stores/clipDataStore';
import { useDiaperStore } from '../stores/diaperStore';
import { useSessionsStore } from '../stores/sessionsStore';
import { useAuthStore } from '../stores/authStore';
import { ClipFileSelector } from '../components/monitor/ClipFileSelector';
import { ClipTimeline, type BackendTimelineEvent } from '../components/monitor/ClipTimeline';
import { ClipDataTable } from '../components/monitor/ClipDataTable';
import { ADCVisualization } from '../components/monitor/ADCVisualization';
import { LiquidAdditionForm } from '../components/monitor/LiquidAdditionForm';
import { AnnotationPanel } from '../components/monitor/AnnotationPanel';
import { AnnotationForm } from '../components/monitor/AnnotationForm';
import { ToastContainer } from '../components/common/Toast';
import { getLiquidAdditions, type LiquidAddition, type Annotation, type ClipDataPoint, type BackendDiaper } from '../lib/api-clip-monitor';
import { calculateWetnessVolume } from '../lib/utils';
import type { Criterion } from '../lib/types';
import { useDiaperSelection } from '../hooks/useDiaperSelection';
import { useInspectorUrlSync } from '../hooks/useInspectorUrlSync';
import { DiaperSelector } from '../components/inspector/DiaperSelector';
import { DiaperModelInfo } from '../components/inspector/DiaperModelInfo';
import { CriterionSelector } from '../components/inspector/CriterionSelector';
import { InspectorToolbar } from '../components/inspector/InspectorToolbar';

interface ToastMessage {
  id: string;
  message: string;
  type?: 'info' | 'success' | 'warning' | 'error';
}

const LOG = (msg: string, ...args: unknown[]) => {
  const serialized = args.map(a => (typeof a === 'object' && a !== null) ? JSON.stringify(a) : a);
  console.log(`[Inspector] ${msg}`, ...serialized);
};

export const DiaperInspectorPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Read URL params for local use (criterion/phase effects, mount log, JSX)
  const initialClipParam = searchParams.get('clip');
  const initialCriterionParam = searchParams.get('criterion');
  const initialPhaseParam = searchParams.get('phase');
  const urlDiaperParam = searchParams.get('diaper');
  const urlDiaperStartParam = searchParams.get('diaperStart');

  const sites = useAuthStore((state) => state.sites);
  const { currentSite, currentNovaAccountId, setCurrentSite, loadSessions } = useSessionsStore();
  const { diaperModels, localModelOverrides, getDiaperModel, loadDiaperModels } = useDiaperStore();
  
  // Liquid addition form state
  const [isLiquidFormOpen, setIsLiquidFormOpen] = useState(false);
  const [liquidAdditions, setLiquidAdditions] = useState<LiquidAddition[]>([]);

  // Annotation form state
  const [isAnnotationFormOpen, setIsAnnotationFormOpen] = useState(false);
  const [editingAnnotation, setEditingAnnotation] = useState<Annotation | null>(null);

  // Custom diaper dropdown state
  const [isDiaperDropdownOpen, setIsDiaperDropdownOpen] = useState(false);
  const diaperDropdownRef = useRef<HTMLDivElement>(null);

  // Override diaper model state
  const [overriddenDiaperModelCode, setOverriddenDiaperModelCode] = useState<number | null>(null);

  // Track whether the user has manually overridden the auto-selected criterion (resets on diaper change)
  const [criterionOverriddenByUser, setCriterionOverriddenByUser] = useState(false);


  const handleEditAnnotation = React.useCallback((annotation: Annotation) => {
    setEditingAnnotation(annotation);
    setIsAnnotationFormOpen(true);
  }, []);

  const handleCloseAnnotationForm = React.useCallback(() => {
    setIsAnnotationFormOpen(false);
    setEditingAnnotation(null);
  }, []);

  // Toast notifications
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const addToast = React.useCallback((message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);
  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };
  
  // Page-level error state for init failures (API calls outside clipDataStore)
  const [initError, setInitError] = useState<string | null>(null);

  const {
    serverOnline,
    serverConfig,
    checkingServer,
    error: storeError,
    clearError,
    availableClips,
    selectedClipAddress,
    selectClip,
    clipData,
    loadingData,
    clipDataLoadLimit,
    autoRefresh,
    refreshInterval,
    availableCriteria,
    loadingCriteria,
    currentCriterion,
    currentPhase,
    loadingCriterion,
    changeNeededPercent,
    brushSelection,
    selectedDiaperCounter,
    useDynamicRange,
    useSideBySideLayout,
    dataResolution,
    checkServer,
    loadClipData,
    loadClipDataFull,
    toggleAutoRefresh,
    toggleProxy,
    loadAvailableCriteria,
    loadCriterion,
    setCriterionDirect,
    clearCriterion,
    setBrushSelection,
    setTimelineFilterPreset,
    zoomIn,
    zoomOut,
    resetZoom,
    setSelectedDiaperCounter,
    setUseDynamicRange,
    setUseSideBySideLayout,
    setDataResolution,
    annotations,
    loadAnnotations,
    firmwareConstants,
    backendMeta,
  } = useClipDataStore();

  const displayError = storeError || initError;

  // Auto-refresh interval
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load liquid additions when clip is selected
  const loadLiquidAdditionsForClip = React.useCallback(async () => {
    if (selectedClipAddress) {
      try {
        const additions = await getLiquidAdditions(selectedClipAddress);
        setLiquidAdditions(additions);
      } catch (error) {
        console.error('Failed to load liquid additions:', error);
        setLiquidAdditions([]);
      }
    }
  }, [selectedClipAddress]);

  // Log once on mount
  useEffect(() => {
    LOG('Page MOUNT', { clip: initialClipParam, diaper: urlDiaperParam, diaperStart: urlDiaperStartParam, criterion: initialCriterionParam, phase: initialPhaseParam });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize site if not set (same as Simulator)
  useEffect(() => {
    if (sites.length > 0 && !currentSite) {
      setCurrentSite(sites[0]);
    }
  }, [sites, currentSite, setCurrentSite]);

  // Load sessions once site is set (same as Simulator)
  useEffect(() => {
    if (currentSite) {
      loadSessions().catch((err: any) => {
        console.error('Failed to load sessions:', err);
        if (err.status !== 401 && err.status !== 403) {
          setInitError(`Failed to load sessions: ${err.message || 'Unknown error'}`);
        }
      });
    }
  }, [currentSite, loadSessions]);

  useEffect(() => {
    checkServer();
  }, [checkServer]);

  // Diaper-models is account-scoped at the proxy. Reload whenever the active
  // Nova account changes; the store caches per-account so this is cheap.
  useEffect(() => {
    if (!currentNovaAccountId) return;
    loadDiaperModels(currentNovaAccountId).catch((err: any) => {
      console.error('Failed to load diaper models:', err);
      if (err.status !== 401 && err.status !== 403) {
        setInitError(`Failed to load diaper models: ${err.message || 'Unknown error'}`);
      }
    });
  }, [currentNovaAccountId, loadDiaperModels]);

  // Load criteria when site is available
  useEffect(() => {
    if (currentSite) {
      loadAvailableCriteria(currentSite).catch((err: any) => {
        console.error('Failed to load criteria:', err);
      });
    }
  }, [currentSite, loadAvailableCriteria]);

  // Setup auto-refresh
  // Load liquid additions when clip data is loaded
  useEffect(() => {
    if (selectedClipAddress && clipData.length > 0) {
      loadLiquidAdditionsForClip();
    }
  }, [selectedClipAddress, clipData.length, loadLiquidAdditionsForClip]);

  // Filter annotations by selected diaper counter (for timeline markers)
  const filteredAnnotations = React.useMemo(() => {
    if (selectedDiaperCounter === null) return annotations;
    return annotations.filter(
      (a) => a.diaperCounter === null || a.diaperCounter === selectedDiaperCounter
    );
  }, [annotations, selectedDiaperCounter]);

  // Filter liquid additions by selected diaper counter
  const filteredLiquidAdditions = React.useMemo(() => {
    if (selectedDiaperCounter === null) return liquidAdditions;
    // Filter to only include additions for the selected diaper counter
    // Only show liquid additions that explicitly match the selected diaper counter
    return liquidAdditions.filter(
      (addition) => addition.diaperCounter === selectedDiaperCounter
    );
  }, [liquidAdditions, selectedDiaperCounter]);

  useEffect(() => {
    if (autoRefresh && selectedClipAddress && serverOnline) {
      intervalRef.current = setInterval(() => {
        loadClipData();
      }, refreshInterval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [autoRefresh, selectedClipAddress, serverOnline, refreshInterval, loadClipData]);

  // Logical diaper grouping — aligned with backend (insert_packet.sql) and firmware lifecycle:
  //
  // Backend closes diaper on: status 1 (DETACHED), 6 (IDENTIFYING), counter change, model change
  // Backend opens diaper on:  status 0, 3, 4, 5, 30 (attached) when no open diaper
  // Backend does NOT act on:  status 2 (shorted, stays attached), 7, 8 (reboot), 9 (sleep), -1/255 (reboot)
  //
  // Firmware increments diaperCounter on every successful detach→identify→attach cycle
  // (scanAndSetDiaperType → gcDiaperTypeDetected → diaperCounter++)
  //
  // Grouping: one diaper per counter value. Within each counter:
  // - Only ONE detached event kept (the first one, marking removal)
  // - Trailing IDENTIFYING records assigned to the NEXT counter (they represent identification of the next diaper)
  // - Sleep (9) does NOT end the diaper (backend doesn't close on sleep)
  const logicalDiaperGroups = React.useMemo<Record<number, ClipDataPoint[]>>(() => {
    if (clipData.length === 0) return {};

    // Status codes aligned with BleAd.java / firmware wetsens_algorithm.c
    const ATTACHED_STATUSES = new Set([0, 3, 4, 5, 30]); // Backend opens diaper on these
    const WITHIN_DIAPER_STATUSES = new Set([2, 7, 8, 9]); // Don't trigger close/open; 2=shorted, 7/8=reboot, 9=sleep
    const STATUS_DETACHED = 1;   // Backend closes diaper
    const STATUS_IDENTIFYING = 6; // Backend closes diaper

    // First pass: group raw records by counter, sorted chronologically
    const rawByCounter: Record<number, ClipDataPoint[]> = {};
    const counterOrder: number[] = [];
    for (const rec of clipData) {
      const c = rec.parsed.diaperCounter;
      if (!rawByCounter[c]) {
        rawByCounter[c] = [];
        counterOrder.push(c);
      }
      rawByCounter[c].push(rec);
    }
    counterOrder.sort((a, b) => {
      const ta = new Date(rawByCounter[a][0].timestamp).getTime();
      const tb = new Date(rawByCounter[b][0].timestamp).getTime();
      return ta - tb;
    });

    const result: Record<number, ClipDataPoint[]> = {};

    for (let ci = 0; ci < counterOrder.length; ci++) {
      const counter = counterOrder[ci];
      const nextCounter = ci < counterOrder.length - 1 ? counterOrder[ci + 1] : null;
      const records = rawByCounter[counter];

      const kept: ClipDataPoint[] = [];
      const trailingIdentifying: ClipDataPoint[] = [];
      let hasSeenAttached = false;
      let detachedIncluded = false;
      let diaperEnded = false;

      for (const rec of records) {
        const status = rec.parsed.statusCode;

        if (diaperEnded) {
          if (status === STATUS_IDENTIFYING) {
            trailingIdentifying.push(rec);
          }
          continue;
        }

        if (ATTACHED_STATUSES.has(status)) {
          hasSeenAttached = true;
          if (trailingIdentifying.length > 0) {
            kept.push(...trailingIdentifying);
            trailingIdentifying.length = 0;
          }
          kept.push(rec);
          detachedIncluded = false;
        } else if (status === STATUS_DETACHED) {
          if (hasSeenAttached && !detachedIncluded) {
            kept.push(rec);
            detachedIncluded = true;
            diaperEnded = true;
          }
          trailingIdentifying.length = 0;
        } else if (status === STATUS_IDENTIFYING) {
          trailingIdentifying.push(rec);
        } else if (WITHIN_DIAPER_STATUSES.has(status)) {
          // Sleep, shorted, reboot statuses: keep as part of diaper (backend doesn't close on these)
          if (hasSeenAttached) {
            kept.push(rec);
          }
        } else {
          // Unknown/experimental status codes — keep if we've seen attached data
          if (hasSeenAttached) {
            kept.push(rec);
          }
        }
      }

      if (kept.length > 0) {
        result[counter] = kept;
      }

      // Trailing IDENTIFYING records belong to the next diaper (firmware is identifying the next diaper)
      if (trailingIdentifying.length > 0 && nextCounter !== null) {
        if (!result[nextCounter]) result[nextCounter] = [];
        const preIdentification: ClipDataPoint[] = [];
        if (kept.length > 0 && kept[kept.length - 1].parsed.statusCode === STATUS_DETACHED) {
          preIdentification.push(kept[kept.length - 1]);
        }
        preIdentification.push(...trailingIdentifying);
        result[nextCounter] = [...preIdentification, ...(result[nextCounter] || [])];
      }
    }

    return result;
  }, [clipData]);

  // Extract unique diaper counters from logical groups
  const availableDiaperCounters = React.useMemo(() => {
    const counters = Object.keys(logicalDiaperGroups).map(Number);
    return counters.sort((a, b) => b - a); // Sort descending (newest first)
  }, [logicalDiaperGroups]);

  // Unified diaper selection: handles URL param matching AND auto-select-newest
  const { linkStatus: diaperLinkStatus, dismissLinkError: dismissDiaperLinkError } = useDiaperSelection({
    clipAddress: selectedClipAddress,
    urlClipParam: initialClipParam,
    urlDiaperParam,
    urlDiaperStartParam,
    availableDiaperCounters,
    logicalDiaperGroups,
    selectedDiaperCounter,
    clipDataLoadLimit,
    loadingData,
    clipDataLength: clipData.length,
    loadClipDataFull,
    setSelectedDiaperCounter,
    addToast,
  });

  // URL sync: applies clip param → store AND syncs store state → URL
  useInspectorUrlSync({
    availableClips,
    selectedClipAddress,
    selectClip,
    selectedDiaperCounter,
    currentCriterion,
    currentPhase,
    diaperLinkStatus,
  });

  // Match backend meta diapers to UI diaper counters using time-overlap
  // Backend .meta.json may include diapers from a ±1 day extraction buffer (see transcript finding),
  // so we match by overlap between the backend diaper's [start_time, end_time] and the UI group's time range.
  const backendDiaperByCounter = React.useMemo<Record<number, BackendDiaper>>(() => {
    if (!backendMeta?.diapers?.length || Object.keys(logicalDiaperGroups).length === 0) return {};

    const result: Record<number, BackendDiaper> = {};
    const usedBackendIds = new Set<number>();

    const counterOrder = Object.keys(logicalDiaperGroups).map(Number).sort((a, b) => {
      const ta = new Date(logicalDiaperGroups[a][0].timestamp).getTime();
      const tb = new Date(logicalDiaperGroups[b][0].timestamp).getTime();
      return ta - tb;
    });

    for (const counter of counterOrder) {
      const records = logicalDiaperGroups[counter];
      if (!records?.length) continue;
      const groupStart = new Date(records[0].timestamp).getTime();
      const groupEnd = new Date(records[records.length - 1].timestamp).getTime();

      let best: BackendDiaper | null = null;
      let bestOverlap = 0;
      let bestDiff = Infinity;

      for (const md of backendMeta.diapers) {
        if (usedBackendIds.has(md.diaper_id)) continue;
        const mdStart = new Date(md.start_time).getTime();
        const mdEnd = new Date(md.end_time || md.close_time).getTime();

        // Calculate overlap between backend diaper and UI group
        const overlapStart = Math.max(groupStart, mdStart);
        const overlapEnd = Math.min(groupEnd, mdEnd);
        const overlap = Math.max(0, overlapEnd - overlapStart);

        if (overlap > bestOverlap) {
          bestOverlap = overlap;
          best = md;
          bestDiff = Math.abs(mdStart - groupStart);
        } else if (overlap === bestOverlap && overlap === 0) {
          // No overlap — fall back to closest start_time
          const diff = Math.abs(mdStart - groupStart);
          if (diff < bestDiff) {
            bestDiff = diff;
            best = md;
          }
        }
      }

      // Accept if there's time overlap, or start times within 30 seconds (covers edge cases)
      if (best && (bestOverlap > 0 || bestDiff < 30000)) {
        result[counter] = best;
        usedBackendIds.add(best.diaper_id);
      }
    }
    return result;
  }, [backendMeta, logicalDiaperGroups]);

  // Track unmatched backend diapers (from extraction buffer or lifecycle mismatches)
  const unmatchedBackendDiapers = React.useMemo<BackendDiaper[]>(() => {
    if (!backendMeta?.diapers?.length) return [];
    const matchedIds = new Set(Object.values(backendDiaperByCounter).map(d => d.diaper_id));
    return backendMeta.diapers.filter(d => !matchedIds.has(d.diaper_id));
  }, [backendMeta, backendDiaperByCounter]);

  // Historical criterion from backend for the currently selected diaper
  const historicalCriterion = React.useMemo(() => {
    if (selectedDiaperCounter === null) return null;
    return backendDiaperByCounter[selectedDiaperCounter]?.criterion ?? null;
  }, [selectedDiaperCounter, backendDiaperByCounter]);

  const historicalCriterionInAvailable = React.useMemo(() => {
    if (!historicalCriterion) return false;
    return availableCriteria.some(c => c.id === historicalCriterion.criterion_id);
  }, [historicalCriterion, availableCriteria]);


  // Arrow key navigation between diapers
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only respond if no input/select/textarea is focused
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (availableDiaperCounters.length < 2 || selectedDiaperCounter === null) return;

      const idx = availableDiaperCounters.indexOf(selectedDiaperCounter);
      if (e.key === 'ArrowLeft' && idx < availableDiaperCounters.length - 1) {
        e.preventDefault();
        setSelectedDiaperCounter(availableDiaperCounters[idx + 1]);
      } else if (e.key === 'ArrowRight' && idx > 0) {
        e.preventDefault();
        setSelectedDiaperCounter(availableDiaperCounters[idx - 1]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [availableDiaperCounters, selectedDiaperCounter, setSelectedDiaperCounter]);

  // Close diaper dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (diaperDropdownRef.current && !diaperDropdownRef.current.contains(e.target as Node)) {
        setIsDiaperDropdownOpen(false);
      }
    };
    if (isDiaperDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDiaperDropdownOpen]);

  // Filter clip data by selected diaper counter (using logical groups)
  const filteredByDiaperCounter = React.useMemo(() => {
    if (selectedDiaperCounter === null) return [];
    return logicalDiaperGroups[selectedDiaperCounter] || [];
  }, [selectedDiaperCounter, logicalDiaperGroups]);

  // Auto-adjust data resolution to target ~1000 points on every diaper change or data load
  const hasManuallySetResolutionRef = useRef<boolean>(false);
  const prevDiaperCounterRefForResolution = useRef<number | null>(null);
  const prevFilteredLengthRef = useRef<number>(0);
  const TARGET_GRAPH_POINTS = 50;
  useEffect(() => {
    const diaperChanged = selectedDiaperCounter !== prevDiaperCounterRefForResolution.current;
    const filteredLength = filteredByDiaperCounter.length;
    const dataLengthChanged = filteredLength !== prevFilteredLengthRef.current && filteredLength > 0;
    prevFilteredLengthRef.current = filteredLength;

    if (diaperChanged) {
      prevDiaperCounterRefForResolution.current = selectedDiaperCounter;
      setOverriddenDiaperModelCode(null);
      setCriterionOverriddenByUser(false);
      hasManuallySetResolutionRef.current = false;
    }

    if (hasManuallySetResolutionRef.current) return;
    if (!diaperChanged && !dataLengthChanged) return;

    if (filteredLength > TARGET_GRAPH_POINTS) {
      const pct = Math.max(1, Math.round((TARGET_GRAPH_POINTS / filteredLength) * 100));
      LOG('Auto-resolution: targeting ~1000 points', { filteredLength, pct, resultingPoints: Math.round(filteredLength * pct / 100), diaperChanged, selectedDiaperCounter });
      setDataResolution(pct);
    } else {
      setDataResolution(100);
    }
  }, [selectedDiaperCounter, filteredByDiaperCounter.length, setDataResolution]);

  // Downsample data based on resolution setting for charting
  const downsampledData = React.useMemo(() => {
    if (dataResolution === 100 || filteredByDiaperCounter.length <= 2) {
      return filteredByDiaperCounter;
    }
    
    const ratio = dataResolution / 100;
    let acc = 0;
    return filteredByDiaperCounter.filter((_, index) => {
      // Always keep first and last
      if (index === 0 || index === filteredByDiaperCounter.length - 1) return true;
      acc += ratio;
      if (acc >= 1) {
        acc -= 1;
        return true;
      }
      return false;
    });
  }, [filteredByDiaperCounter, dataResolution]);

  // Auto-detect diaper model from filtered clip data
  // Find first record with a non-zero model (skip leading Identifying records which may have model=0)
  const currentDiaperModel = React.useMemo(() => {
    if (overriddenDiaperModelCode !== null) {
      return getDiaperModel(overriddenDiaperModelCode);
    }
    if (filteredByDiaperCounter.length === 0) return null;
    const activeRec = filteredByDiaperCounter.find(r => r.parsed.diaperModel !== 0);
    if (!activeRec) return getDiaperModel(filteredByDiaperCounter[0].parsed.diaperModel);
    return getDiaperModel(activeRec.parsed.diaperModel);
  }, [filteredByDiaperCounter, getDiaperModel, overriddenDiaperModelCode]);

  // Auto-apply the historical criterion from backend meta when a diaper is selected
  const prevAutoApplyCounterRef = useRef<number | null>(null);
  useEffect(() => {
    if (criterionOverriddenByUser) return;
    if (selectedDiaperCounter === null || !currentDiaperModel) return;
    if (selectedDiaperCounter === prevAutoApplyCounterRef.current) return;
    prevAutoApplyCounterRef.current = selectedDiaperCounter;

    const bd = backendDiaperByCounter[selectedDiaperCounter];
    const bc = bd?.criterion;
    if (!bc?.config) return; // no historical criterion — fallback effect below will handle it

    const matchInAvailable = availableCriteria.find(c => c.id === bc.criterion_id);
    if (matchInAvailable) {
      loadCriterion(matchInAvailable.id, currentDiaperModel.code);
    } else {
      const synthetic: Criterion = {
        id: bc.criterion_id,
        name: bc.name,
        content: bc.config as Criterion['content'],
      };
      setCriterionDirect(synthetic, currentDiaperModel.code);
    }
  }, [selectedDiaperCounter, criterionOverriddenByUser, backendDiaperByCounter, availableCriteria, currentDiaperModel, loadCriterion, setCriterionDirect]);

  // Fallback: auto-select criterion from URL param (or ID 1) when no backend criterion is available
  useEffect(() => {
    if (criterionOverriddenByUser) return;
    if (availableCriteria.length === 0 || currentCriterion || loadingCriterion || !currentDiaperModel) return;

    // Skip fallback if the backend has a criterion for this diaper (the effect above handles it)
    if (selectedDiaperCounter !== null) {
      const bd = backendDiaperByCounter[selectedDiaperCounter];
      if (bd?.criterion?.config) return;
    }

    const targetId = initialCriterionParam ? parseInt(initialCriterionParam, 10) : 1;
    const match = availableCriteria.find(c => c.id === targetId) || availableCriteria.find(c => c.id === 1);
    if (match) {
      loadCriterion(match.id, currentDiaperModel.code);
    }
  }, [availableCriteria, currentCriterion, loadingCriterion, currentDiaperModel, loadCriterion, initialCriterionParam, criterionOverriddenByUser, selectedDiaperCounter, backendDiaperByCounter]);

  // Apply phase from URL param after criterion loads
  const appliedPhaseFromUrlRef = useRef(false);
  useEffect(() => {
    if (!initialPhaseParam || !currentCriterion || appliedPhaseFromUrlRef.current) return;
    const targetPhaseId = parseInt(initialPhaseParam, 10);
    if (isNaN(targetPhaseId)) return;
    const phaseExists = currentCriterion.content.phases.some(p => p.id === targetPhaseId);
    if (phaseExists && currentPhase?.id !== targetPhaseId) {
      useClipDataStore.getState().selectPhase(targetPhaseId, currentDiaperModel?.code || null);
    }
    appliedPhaseFromUrlRef.current = true;
  }, [currentCriterion, currentPhase, initialPhaseParam, currentDiaperModel]);

  // Reset the applied-phase flag when criterion changes (so a new URL load can re-apply)
  useEffect(() => {
    appliedPhaseFromUrlRef.current = false;
  }, [initialCriterionParam]);

  // Resolve the active threshold from the current criterion phase for the current diaper model
  const activeThreshold = React.useMemo(() => {
    if (!currentPhase?.thresholds?.length) return null;
    return (
      currentPhase.thresholds.find(t => t.diaperModel === currentDiaperModel?.code) ||
      currentPhase.thresholds.find(t => t.diaperModel == null) ||
      currentPhase.thresholds[0]
    );
  }, [currentPhase, currentDiaperModel]);

  // Detect alarm events across ALL diapers using criterion threshold flags + stabilization debouncing
  // Pooling is always computed -- shown as hypothetical (amber) if not enabled in criterion
  const alarmEventsByDiaper = React.useMemo<Record<number, Array<{ timestamp: Date; reason: string; hypothetical?: boolean }>>>(() => {
    if (!currentCriterion || Object.keys(logicalDiaperGroups).length === 0) return {};

    const stabPeriodMs = (currentCriterion.content.stabilizationPeriod_sec || 0) * 1000;
    const leakWetness = currentDiaperModel?.leakThreshold?.wetnessLevel;
    const leakAdc = currentDiaperModel?.leakThreshold?.adcValues;

    type AlarmType = 'Pooling' | 'Leak Imminent' | 'Wetness Exceeded' | 'ADC Exceeded' | 'Change Needed';
    const enabledChecks: Array<{ type: AlarmType; test: (r: typeof clipData[0]) => boolean; hypothetical?: boolean }> = [];

    // Pooling: always computed. Hypothetical if not enabled in criterion.
    const poolingEnabled = activeThreshold?.leakUnknownStatusEnabled ?? false;
    enabledChecks.push({ type: 'Pooling', test: r => r.parsed.statusCode === 30, hypothetical: !poolingEnabled });

    if (activeThreshold?.leakClipStatusEnabled)
      enabledChecks.push({ type: 'Leak Imminent', test: r => r.parsed.statusCode === 5 });
    if (activeThreshold?.leakLevelEnabled && leakWetness != null)
      enabledChecks.push({ type: 'Wetness Exceeded', test: r => r.parsed.wetnessLevel >= leakWetness! });
    if (activeThreshold?.leakAdcValuesEnabled && leakAdc)
      enabledChecks.push({ type: 'ADC Exceeded', test: r => r.parsed.adcValues.some((v, i) => leakAdc![i] != null && v >= leakAdc![i]) });

    if (changeNeededPercent && currentDiaperModel) {
      const changeNeededVolume = currentDiaperModel.capacity * (changeNeededPercent / 100);
      enabledChecks.push({
        type: 'Change Needed',
        test: r => {
          const vol = calculateWetnessVolume(r.parsed.wetnessLevel, currentDiaperModel!);
          return (vol.volume || 0) >= changeNeededVolume;
        },
      });
    }

    const result: Record<number, Array<{ timestamp: Date; reason: string; hypothetical?: boolean }>> = {};

    for (const counterStr of Object.keys(logicalDiaperGroups)) {
      const counter = Number(counterStr);
      const records = logicalDiaperGroups[counter];
      const events: Array<{ timestamp: Date; reason: string; hypothetical?: boolean }> = [];

      for (const check of enabledChecks) {
        let conditionStart: Date | null = null;
        let alarmEmitted = false;

        for (const rec of records) {
          const ts = new Date(rec.timestamp);
          if (check.test(rec)) {
            if (!conditionStart) conditionStart = ts;
            if (!alarmEmitted && (ts.getTime() - conditionStart.getTime()) >= stabPeriodMs) {
              events.push({ timestamp: ts, reason: check.type, hypothetical: check.hypothetical });
              alarmEmitted = true;
            }
          } else {
            conditionStart = null;
            alarmEmitted = false;
          }
        }
      }

      if (events.length > 0) {
        events.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        result[counter] = events;
      }
    }

    return result;
  }, [logicalDiaperGroups, activeThreshold, currentCriterion, currentDiaperModel, changeNeededPercent]);

  // Alarm events for the currently selected diaper (used by timeline and summary)
  const selectedDiaperAlarms = React.useMemo(() => {
    if (selectedDiaperCounter === null) return [];
    return alarmEventsByDiaper[selectedDiaperCounter] || [];
  }, [alarmEventsByDiaper, selectedDiaperCounter]);

  // Build backend timeline events for the selected diaper
  const selectedBackendEvents = React.useMemo<BackendTimelineEvent[]>(() => {
    if (selectedDiaperCounter === null) return [];
    const bd = backendDiaperByCounter[selectedDiaperCounter];
    if (!bd) return [];

    const events: BackendTimelineEvent[] = [];

    for (const e of bd.emictions) {
      events.push({
        timestamp: new Date(e.time),
        type: 'emiction',
        label: 'Emiction',
        detail: `+${Math.round(e.delta_ml)}mL`,
      });
    }

    const a = bd.alerts;
    if (a.wet_alert_time) events.push({ timestamp: new Date(a.wet_alert_time), type: 'wet_alert', label: 'Wet Alert' });
    if (a.leak_alert_time) events.push({ timestamp: new Date(a.leak_alert_time), type: 'leak_alert', label: 'Leak Alert' });
    if (a.pool_alert_time) events.push({ timestamp: new Date(a.pool_alert_time), type: 'pool_alert', label: 'Pool Alert' });
    if (a.pool_time) events.push({ timestamp: new Date(a.pool_time), type: 'pool_detected', label: 'Pool Detected' });
    if (a.leak_time) events.push({ timestamp: new Date(a.leak_time), type: 'leak_detected', label: 'Leak Detected' });
    if (a.remove_pool_time) events.push({ timestamp: new Date(a.remove_pool_time), type: 'pool_cleared', label: 'Pool Cleared' });

    return events.sort((x, y) => x.timestamp.getTime() - y.timestamp.getTime());
  }, [selectedDiaperCounter, backendDiaperByCounter]);

  // Check for invalid diaper condition: status OK (0) but model is 0 or unknown
  // Only check the LATEST measurement, not historical ones
  const hasInvalidDiaperAlert = React.useMemo(() => {
    if (filteredByDiaperCounter.length === 0) return false;
    const latestRecord = filteredByDiaperCounter[filteredByDiaperCounter.length - 1]; // Only check latest
    return latestRecord.parsed.statusCode === 0 && (latestRecord.parsed.diaperModel === 0 || !getDiaperModel(latestRecord.parsed.diaperModel));
  }, [filteredByDiaperCounter, getDiaperModel]);

  // Detect inconsistent diaper counter data (counter appears, disappears, then reappears)
  const diaperCounterWarning = React.useMemo(() => {
    if (!selectedDiaperCounter || clipData.length === 0) return null;

    // Track all time periods where this counter appears
    const periods: Array<{ start: number; end: number }> = [];
    let inPeriod = false;
    let periodStart = 0;

    for (let i = 0; i < clipData.length; i++) {
      const isSelectedCounter = clipData[i].parsed.diaperCounter === selectedDiaperCounter;
      
      if (isSelectedCounter && !inPeriod) {
        // Start of a new period
        periodStart = i;
        inPeriod = true;
      } else if (!isSelectedCounter && inPeriod) {
        // End of a period
        periods.push({ start: periodStart, end: i - 1 });
        inPeriod = false;
      }
    }
    
    // Close the last period if still open
    if (inPeriod) {
      periods.push({ start: periodStart, end: clipData.length - 1 });
    }

    // If we have more than one period, data is inconsistent
    if (periods.length > 1) {
      return {
        message: `⚠️ Inconsistent Data: Diaper #${selectedDiaperCounter} appears in ${periods.length} separate time periods. This indicates invalid data from the clip/relay.`,
        periods,
      };
    }

    return null;
  }, [clipData, selectedDiaperCounter]);

  // Find baseline measurement and calculate diaper age
  // Baseline = first OK status (0) for the selected diaper
  const baselineInfo = React.useMemo(() => {
    if (filteredByDiaperCounter.length === 0) return null;

    // For the filtered data (single diaper counter), baseline is the first OK status
    const firstOk = filteredByDiaperCounter.find(r => r.parsed.statusCode === 0);
    if (!firstOk) return null;
    
    return {
      timestamp: new Date(firstOk.timestamp),
      adcValues: firstOk.parsed.adcValues,
      recordIndex: filteredByDiaperCounter.indexOf(firstOk),
    };
  }, [filteredByDiaperCounter]);

  // Calculate diaper age from baseline
  const diaperAge = React.useMemo(() => {
    if (!baselineInfo || filteredByDiaperCounter.length === 0) return null;
    
    const latestTime = new Date(filteredByDiaperCounter[filteredByDiaperCounter.length - 1].timestamp);
    const ageMs = latestTime.getTime() - baselineInfo.timestamp.getTime();
    
    const hours = Math.floor(ageMs / (1000 * 60 * 60));
    const minutes = Math.floor((ageMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ageMs % (1000 * 60)) / 1000);
    
    return { hours, minutes, seconds, totalMs: ageMs };
  }, [baselineInfo, filteredByDiaperCounter]);

  const handleToggleProxy = async () => {
    try {
      await toggleProxy();
    } catch (error) {
      alert('Failed to toggle proxy');
    }
  };

  const handleCriterionChange = async (criterionId: string) => {
    if (!criterionId) {
      clearCriterion();
      return;
    }
    
    setCriterionOverriddenByUser(true);

    try {
      const diaperModelCode = currentDiaperModel?.code || null;
      await loadCriterion(parseInt(criterionId, 10), diaperModelCode);
    } catch (error) {
      alert('Failed to load criterion');
    }
  };

  const handleRevertToHistoricalCriterion = () => {
    setCriterionOverriddenByUser(false);
    prevAutoApplyCounterRef.current = null; // force the auto-apply effect to re-run
  };

  const filteredClipData = React.useMemo(() => {
    if (filteredByDiaperCounter.length === 0) return [];
    
    // If no brush selection, show all data
    if (brushSelection.startTime === null && brushSelection.endTime === null) {
      return filteredByDiaperCounter;
    }

    // Filter by brush selection
    const startTime = brushSelection.startTime || new Date(filteredByDiaperCounter[0].timestamp);
    const endTime = brushSelection.endTime || new Date(filteredByDiaperCounter[filteredByDiaperCounter.length - 1].timestamp);

    return filteredByDiaperCounter.filter((record) => {
      const recordTime = new Date(record.timestamp);
      return recordTime >= startTime && recordTime <= endTime;
    });
  }, [filteredByDiaperCounter, brushSelection]);

  // Latest data point for ADC visualization
  const latestDataPoint = filteredClipData.length > 0
    ? filteredClipData[filteredClipData.length - 1]
    : null;

  return (
    <div className="space-y-6">
      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {displayError && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-4 flex items-start gap-3">
          <span className="text-red-600 text-xl flex-shrink-0">⚠</span>
          <div className="flex-1">
            <h3 className="font-semibold text-red-900">Error</h3>
            <p className="text-sm text-red-800 mt-1">{displayError}</p>
          </div>
          <button
            onClick={() => { clearError(); setInitError(null); }}
            className="text-red-400 hover:text-red-600 text-lg leading-none flex-shrink-0"
          >
            ×
          </button>
        </div>
      )}
      
      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {initialClipParam && (
              <button
                onClick={() => navigate(-1)}
                className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium"
                title="Back to Clip Lifetime"
              >
                ← Back
              </button>
            )}
            <h2 className="text-2xl font-bold">Clip Data Monitor</h2>
          </div>
          <div className="flex items-center gap-4">
            {/* Server Status */}
            <div className="flex items-center gap-2">
              <div
                className={`w-3 h-3 rounded-full ${
                  checkingServer
                    ? 'bg-yellow-400 animate-pulse'
                    : serverOnline
                    ? 'bg-green-500'
                    : 'bg-red-500'
                }`}
              />
              <span className="text-sm font-medium">
                {checkingServer
                  ? 'Checking...'
                  : serverOnline
                  ? 'Server Online'
                  : 'Server Offline'}
              </span>
            </div>

            {/* Proxy Toggle */}
            {serverConfig && serverOnline && (
              <button
                onClick={handleToggleProxy}
                className={`px-3 py-1 rounded text-sm font-medium ${
                  serverConfig.proxyEnabled
                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                {serverConfig.proxyEnabled ? '✓ Forwarding to Backend' : '✗ Local Only'}
              </button>
            )}

            {/* Refresh Server Status */}
            <button
              onClick={checkServer}
              disabled={checkingServer}
              className="px-3 py-1 bg-gray-100 text-gray-800 rounded hover:bg-gray-200 text-sm disabled:opacity-50"
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        <div className="bg-blue-50 border-l-4 border-primary p-4 rounded">
          <p className="text-sm text-gray-700">
            <strong>📡 Live Data Monitoring:</strong> This page displays sensor data received from
            clips via relays. Configure your relay to send packets to{' '}
            <code className="bg-white px-2 py-1 rounded text-xs">http://localhost:20042</code> (or
            via ngrok).
          </p>
          <p className="text-sm text-gray-700 mt-2">
            <strong>💡 Proxy Mode:</strong> When enabled, packets are forwarded to the real backend
            (validation.wetsens.com) in addition to being stored locally.
          </p>
        </div>
      </div>

      {/* Clip File Selector */}
      <ClipFileSelector />

      {/* Selected Clip Data */}
      {selectedClipAddress && (
        <>
          {/* Controls */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">
                Viewing:{' '}
                {(() => {
                  const clip = availableClips.find(c => c.clipAddress === selectedClipAddress);
                  if (clip?.displayLabel) {
                    return (
                      <>
                        <span className="text-primary">{clip.displayLabel}</span>
                        <span className="font-mono text-sm text-gray-500 ml-2">[{selectedClipAddress}]</span>
                      </>
                    );
                  }
                  return <span className="font-mono text-primary">{selectedClipAddress}</span>;
                })()}
              </h3>
              <div className="flex items-center gap-4">
                {/* Auto-refresh toggle */}
                <button
                  onClick={toggleAutoRefresh}
                  className={`px-3 py-2 rounded text-sm font-medium ${
                    autoRefresh
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  {autoRefresh ? '⏸️ Stop Auto-Refresh' : '▶️ Start Auto-Refresh'}
                </button>

                {/* Manual refresh */}
                <button
                  onClick={() => loadClipData()}
                  disabled={loadingData}
                  className="px-3 py-2 bg-primary text-white rounded hover:bg-primary-dark disabled:opacity-50 text-sm"
                >
                  {loadingData ? '⏳ Loading...' : '🔄 Refresh Now'}
                </button>
                {/* Load full data when truncated (large clips) */}
                {clipDataLoadLimit !== null && selectedClipAddress && (() => {
                  const meta = availableClips.find((c) => c.clipAddress === selectedClipAddress);
                  const total = meta?.recordCount ?? 0;
                  if (total <= clipDataLoadLimit) return null;
                  return (
                    <button
                      onClick={() => loadClipDataFull()}
                      disabled={loadingData}
                      className="px-3 py-2 bg-amber-100 text-amber-800 rounded hover:bg-amber-200 disabled:opacity-50 text-sm"
                      title={`Load all ~${total.toLocaleString()} records (may be slow)`}
                    >
                      {loadingData ? '⏳ Loading...' : `📥 Load full data (~${total.toLocaleString()} records)`}
                    </button>
                  );
                })()}
              </div>
            </div>

            {clipDataLoadLimit !== null && selectedClipAddress && (() => {
              const meta = availableClips.find((c) => c.clipAddress === selectedClipAddress);
              const total = meta?.recordCount ?? 0;
              if (total <= clipDataLoadLimit) return null;
              return (
                <p className="text-xs text-amber-700 mt-2">
                  📊 Showing last {clipDataLoadLimit.toLocaleString()} of ~{total.toLocaleString()} records. Use &quot;Load full data&quot; to load all (may be slow).
                </p>
              );
            })()}
            {autoRefresh && (
              <p className="text-xs text-gray-600 mt-2">
                ⚡ Auto-refreshing every {refreshInterval / 1000} second
                {refreshInterval !== 1000 ? 's' : ''}
              </p>
            )}

            <DiaperSelector
              selectedClipAddress={selectedClipAddress}
              loadingData={loadingData}
              availableDiaperCounters={availableDiaperCounters}
              diaperLinkStatus={diaperLinkStatus}
              dismissDiaperLinkError={dismissDiaperLinkError}
              selectedDiaperCounter={selectedDiaperCounter}
              setSelectedDiaperCounter={setSelectedDiaperCounter}
              isDiaperDropdownOpen={isDiaperDropdownOpen}
              setIsDiaperDropdownOpen={setIsDiaperDropdownOpen}
              diaperDropdownRef={diaperDropdownRef}
              logicalDiaperGroups={logicalDiaperGroups}
              getDiaperModel={getDiaperModel}
              annotations={annotations}
              alarmEventsByDiaper={alarmEventsByDiaper}
              backendDiaperByCounter={backendDiaperByCounter}
              unmatchedBackendDiapers={unmatchedBackendDiapers}
              backendMeta={backendMeta}
              filteredByDiaperCounter={filteredByDiaperCounter}
              diaperCounterWarning={diaperCounterWarning}
              clipData={clipData}
            />

            <div className="flex flex-col md:flex-row gap-4">
              <DiaperModelInfo
                overriddenDiaperModelCode={overriddenDiaperModelCode}
                setOverriddenDiaperModelCode={setOverriddenDiaperModelCode}
                diaperModels={diaperModels}
                localModelOverrides={localModelOverrides}
                hasInvalidDiaperAlert={hasInvalidDiaperAlert}
                currentDiaperModel={currentDiaperModel}
                baselineInfo={baselineInfo}
                diaperAge={diaperAge}
                filteredByDiaperCounter={filteredByDiaperCounter}
                firmwareConstants={firmwareConstants}
                selectedDiaperCounter={selectedDiaperCounter}
                backendDiaperByCounter={backendDiaperByCounter}
                currentCriterion={currentCriterion}
              />
              <CriterionSelector
                currentCriterion={currentCriterion}
                currentPhase={currentPhase}
                currentDiaperModel={currentDiaperModel}
                availableCriteria={availableCriteria}
                loadingCriteria={loadingCriteria}
                loadingCriterion={loadingCriterion}
                historicalCriterion={historicalCriterion}
                historicalCriterionInAvailable={historicalCriterionInAvailable}
                criterionOverriddenByUser={criterionOverriddenByUser}
                setCriterionOverriddenByUser={setCriterionOverriddenByUser}
                handleCriterionChange={handleCriterionChange}
                handleRevertToHistoricalCriterion={handleRevertToHistoricalCriterion}
                changeNeededPercent={changeNeededPercent}
                activeThreshold={activeThreshold}
                selectedDiaperAlarms={selectedDiaperAlarms}
              />
            </div>
          </div>

          <InspectorToolbar
            selectedDiaperCounter={selectedDiaperCounter}
            currentDiaperModel={currentDiaperModel}
            overriddenDiaperModelCode={overriddenDiaperModelCode}
            zoomIn={zoomIn}
            zoomOut={zoomOut}
            resetZoom={resetZoom}
            setTimelineFilterPreset={setTimelineFilterPreset}
            useDynamicRange={useDynamicRange}
            setUseDynamicRange={setUseDynamicRange}
            onOpenLiquidForm={() => setIsLiquidFormOpen(true)}
            onOpenAnnotationForm={() => setIsAnnotationFormOpen(true)}
            dataResolution={dataResolution}
            onResolutionChange={(value) => {
              hasManuallySetResolutionRef.current = true;
              setDataResolution(value);
            }}
            useSideBySideLayout={useSideBySideLayout}
            setUseSideBySideLayout={setUseSideBySideLayout}
          />

          {/* Graphs Section - Side by Side on Wide Screens (when enabled) */}
          <div className={`grid grid-cols-1 gap-6 ${useSideBySideLayout ? 'xl:grid-cols-2 xl:items-stretch' : ''}`}>
            {/* ADC Visualization - receives unfiltered data, will filter internally based on timeline range */}
            <ADCVisualization
              latestData={latestDataPoint}
              historyData={downsampledData}
              leakAdcThresholds={currentDiaperModel?.leakThreshold?.adcValues || null}
              diaperModel={currentDiaperModel}
              baselineAdcValues={baselineInfo?.adcValues || null}
              useDynamicRange={useDynamicRange}
              liquidAdditions={filteredLiquidAdditions}
              brushSelection={brushSelection}
            />

            {/* Timeline Graph */}
            {downsampledData.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-4 flex flex-col h-full">
                <ClipTimeline 
                  data={downsampledData}
                  diaperModel={currentDiaperModel}
                  changeNeededPercent={changeNeededPercent}
                  brushSelection={brushSelection}
                  onBrushChange={setBrushSelection}
                  useDynamicRange={useDynamicRange}
                  liquidAdditions={filteredLiquidAdditions}
                  annotations={filteredAnnotations}
                  alarmEvents={selectedDiaperAlarms}
                  backendEvents={selectedBackendEvents}
                  stabilizationPeriodSec={currentCriterion?.content.stabilizationPeriod_sec}
                />
              </div>
            )}
          </div>

          {/* Annotation Panel - below graphs */}
          <AnnotationPanel 
            onAddAnnotation={() => setIsAnnotationFormOpen(true)} 
            onEditAnnotation={handleEditAnnotation}
          />

          {/* Data Table - Show filtered packets (respects diaper selection and brush zoom) */}
          <ClipDataTable data={filteredClipData} diaperModel={currentDiaperModel} />
        </>
      )}

      {/* Empty State */}
      {!selectedClipAddress && serverOnline && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <p className="text-6xl mb-4">📊</p>
          <p className="text-xl font-semibold text-gray-700 mb-2">
            Select a clip to view data
          </p>
          <p className="text-gray-600">
            Choose a clip from the list above to see its measurement timeline
          </p>
        </div>
      )}

      {/* Liquid Addition Form - Draggable Overlay */}
      {selectedClipAddress && (
        <LiquidAdditionForm
          clipAddress={selectedClipAddress}
          isOpen={isLiquidFormOpen}
          onClose={() => setIsLiquidFormOpen(false)}
          onAddition={loadLiquidAdditionsForClip}
          diaperCounter={selectedDiaperCounter}
        />
      )}

      {/* Annotation Form - Modal */}
      <AnnotationForm
        isOpen={isAnnotationFormOpen}
        onClose={handleCloseAnnotationForm}
        onSuccess={loadAnnotations}
        editingAnnotation={editingAnnotation}
      />
    </div>
  );
};

